import { company } from './company';
import { products } from './products';
import { brands, masterBrands, additionalBrands } from './brands';
import { teamMembers } from './team';

// The live site is served on www (the bare domain 301-redirects to it). Canonicals, sitemap,
// structured data and share URLs all use this one address.
export const SITE_URL = 'https://www.ojanna.co.za';
export const BUSINESS_ID = `${SITE_URL}/#business`;
export const WEBSITE_ID = `${SITE_URL}/#website`;

const masterBrandNames = masterBrands.map((b) => b.shortName || b.name).join(', ');
const additionalBrandNames = additionalBrands.map((b) => b.shortName || b.name).join(', ');
const allBrandNames = brands.map((b) => b.shortName || b.name).join(', ');
const teamNames = teamMembers.map((m) => m.name).join(', ');

// Core local search phrases. The meta keywords tag carries little ranking weight with Google,
// so these mainly document the targets — the same phrases must appear in titles, headings and copy.
export const CORE_KEYWORDS = [
  'ceiling and drywall supplies George',
  'ceiling suppliers George',
  'drywall suppliers George',
  'ceiling boards George',
  'rhino board George',
  'roof insulation George',
  'plasterboard George',
  'building supplies George',
  'building materials George Western Cape',
  'ceiling supplies Garden Route',
  'drywall supplies Western Cape',
  'OJANNA Ceiling & Drywall Distributors',
];

const productsStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `${company.name} Products`,
  description:
    'Ceiling, drywall, insulation and acoustic supply products including exposed grid (white, black & grey), plasterboard (Gyproc, Siniat, SuperTec), aluminium profiles, Youngman insulation and WoodUpp acoustic wall panels',
  itemListElement: products.map((p, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: p.title,
    description: p.description,
  })),
};

const partnersStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `${company.name} Partner Brands`,
  description: `Master distributor for ${masterBrandNames}. Also distributing ${additionalBrandNames}.`,
  itemListElement: brands.map((b, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: b.name,
    description: b.description,
    url: b.website || undefined,
  })),
};

const teamStructuredData = {
  '@context': 'https://schema.org',
  '@type': 'ItemList',
  name: `${company.name} Team`,
  description: `The team at ${company.name} in George, led by ${company.founder.name}.`,
  itemListElement: teamMembers.map((m, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    item: {
      '@type': 'Person',
      name: m.name,
      jobTitle: m.role,
      description: m.description,
      worksFor: { '@id': BUSINESS_ID },
    },
  })),
};

// Titles get " | OJANNA Ceiling & Drywall Distributors" appended by <SEO />.
// Lead with the search phrase; keep descriptions around 150–160 characters.
export const pageSeo = {
  '/': {
    title: 'Ceiling and Drywall Supplies in George',
    description:
      'Ceiling & drywall supplies in George: ceiling boards, plasterboard, suspended grid, roof insulation & acoustic panels. Delivered Plett to Cape Town.',
    keywords: CORE_KEYWORDS.join(', '),
  },
  '/about': {
    title: 'About Us — 30+ Years Supplying Builders in George',
    description: 'OJANNA Ceiling & Drywall Distributors: George\'s ceiling & drywall supplier, led by Niel Kasselman. 30+ years in the industry, SABISA Accredited.',
    keywords: [
      `about ${company.name}`,
      'ceiling and drywall supplier George',
      'Niel Kasselman',
      'SABISA Accredited supplier',
      'Green Building Council Professional George',
    ].join(', '),
    breadcrumbs: [{ name: 'About Us', path: '/about' }],
  },
  '/products': {
    title: 'Products — Ceiling Boards, Plasterboard, Grid & Insulation',
    description:
      'Ceiling boards, suspended grid, plasterboard, brandering, roof insulation, acoustic panels, plasters & tools — in stock at our George warehouse.',
    keywords: [
      'ceiling products George',
      'drywall products George',
      'plasterboard Gyproc Siniat SuperTec',
      'suspended ceiling grid suppliers',
      'exposed grid ceiling white black grey',
      'steel brandering',
      'ceiling insulation',
      'acoustic panels',
      'jointing compound',
    ].join(', '),
    structuredData: productsStructuredData,
    breadcrumbs: [{ name: 'Products', path: '/products' }],
  },
  '/partners': {
    title: 'SuperTec, ETEX, Siniat & OWA Master Distributor in George',
    description: 'Master distributor for SuperTec, ETEX, Siniat & OWA in George, also supplying Gyproc, Knauf Insulation, Isover, Nutec, WoodUpp & more.',
    keywords: [
      'SuperTec distributor George',
      'Siniat distributor',
      'ETEX distributor',
      'OWA ceiling tiles distributor',
      'Gyproc supplier George',
      allBrandNames,
    ].join(', '),
    structuredData: partnersStructuredData,
    breadcrumbs: [{ name: 'Partners', path: '/partners' }],
  },
  '/contact': {
    title: 'Contact Us & Get a Quote — Joubert Industria, George',
    description: 'Get a quote on ceiling, drywall & insulation supplies. Visit our showroom in Joubert Industria, George, call 064 531 9070 or WhatsApp us.',
    keywords: [
      'ceiling supplier George contact',
      'drywall quote George',
      'plasterboard prices George',
      'building supplies Joubert Industria',
    ].join(', '),
    breadcrumbs: [{ name: 'Contact Us', path: '/contact' }],
  },
  '/team': {
    title: 'Meet the Team',
    description: 'Meet the OJANNA Ceiling & Drywall Distributors team in George — expert advice on ceilings, drywall, plasterboard and insulation.',
    keywords: [`${company.name} team`, teamNames].join(', '),
    structuredData: teamStructuredData,
    breadcrumbs: [
      { name: 'About Us', path: '/about' },
      { name: 'Our Team', path: '/team' },
    ],
  },
  '/delivery-areas': {
    title: 'Delivery Areas — Garden Route, Klein Karoo & Cape Town',
    description:
      'Ceiling & drywall supplies delivered from George to Plettenberg Bay, Knysna, Mossel Bay, Oudtshoorn, the Klein Karoo, Swellendam & Cape Town.',
    keywords: [
      'ceiling supplies delivery Garden Route',
      'drywall supplies Mossel Bay',
      'ceiling supplier Knysna',
      'ceiling supplier Oudtshoorn',
      'drywall supplier Plettenberg Bay',
    ].join(', '),
    breadcrumbs: [{ name: 'Delivery Areas', path: '/delivery-areas' }],
  },
  '/privacy-policy': {
    title: 'Privacy Policy',
    description: `Privacy Policy for ${company.name}. How we collect, use and protect personal information under POPIA when you visit our website or contact us.`,
    breadcrumbs: [{ name: 'Privacy Policy', path: '/privacy-policy' }],
  },
  '/cookie-policy': {
    title: 'Cookie Policy',
    description: `Cookie Policy for ${company.name}. Learn what cookies we use, how consent works, and how to manage your analytics and advertising preferences.`,
    breadcrumbs: [{ name: 'Cookie Policy', path: '/cookie-policy' }],
  },
  '/terms-of-use': {
    title: 'Website Terms of Use',
    description: `Website Terms of Use for ${company.name}. Rules for using ojanna.co.za, product information disclaimers, and governing law.`,
    breadcrumbs: [{ name: 'Terms of Use', path: '/terms-of-use' }],
  },
};
